<script setup lang="ts">
import { useConfirm, VIcons, VPreloader } from '@/shared/ui'
import useModel from './../model/model'
import { useSendControlWidgetCommand } from './../model/api'
import './assets/style.scss'
import { computed, toRef, watch } from 'vue'
import { useSseConnection } from '@/shared/lib'
import { useRoute } from 'vue-router'
import { intersection } from 'lodash'
import { ContactorRange } from '@/entities/Cabinet'
import { useAuth } from '@/entities/Auth'

const props = defineProps({
  objectId: {
    type: Number,
    required: true,
  },
})

const { items, loadData } = useModel()
const { send } = useSendControlWidgetCommand()
const { show } = useConfirm()
const route = useRoute()
const { isPermissions } = useAuth()

const objectId = computed(() => props.objectId || route.params.object_id)

/**
 * Обработка отправки команды
 * @param contactor
 * @param type
 * @param value
 */
const clickHandler = (contactor: number, type: string, value: number) => {
  show({
    confirmFunc: () => send(contactor, type, value, Number(objectId.value)),
  })
}

const { isPending, refetch } = loadData(toRef(props, 'objectId'))

/* Устанавливаем SSE соединение */
const { handler } = useSseConnection(
  async (res: { pin_statuses?: any[]; output_sensors?: any[] }) => {
    /* Обрабатываем обновление при изменении контакторов */
    if (res.pin_statuses !== undefined) {
      const eventTypes = res.pin_statuses.map((item) => item.device_event_type_id)

      if (intersection(eventTypes, ContactorRange).length) refetch()
    }

    /* Обрабатываем значения по управления */
    if (res.output_sensors !== undefined) refetch()
  },
)

watch(
  () => objectId,
  () => {
    handler(`/cabinets/${objectId.value}/statuses`)
  },
  { immediate: true },
)
</script>

<template>
  <div class="c-control-item">
    <template v-if="isPending">
      <VPreloader />
    </template>

    <template v-else>
      <div class="c-control-item__row">
        <div>{{ $t('itf.all') }}</div>
        <div></div>
        <div></div>
        <div
          class="c-control-item__buttons"
          :class="{ disabled: !isPermissions(['DEVICE_CONTROL']) }"
        >
          <VBtn density="compact" color="warning" @click="clickHandler(-1, 'mode', 1)">{{
            $t('itf.auto')
          }}</VBtn>
          <VBtn density="compact" color="warning" @click="clickHandler(-1, 'switch', 1)">{{
            $t('itf.on_short')
          }}</VBtn>
          <VBtn density="compact" color="warning" @click="clickHandler(-1, 'switch', 0)">{{
            $t('itf.off_short')
          }}</VBtn>
        </div>
      </div>

      <div class="c-control-item__row" v-for="item in items" :key="item.id">
        <div class="c-control-item__info">
          {{ `K${item.id}` }}

          <div v-if="item.description" class="c-control-item__description">
            <VTooltip :text="item.description" open-delay="300">
              <template v-slot:activator="{ props }">
                <VIcons v-bind="props" type="info_i" />
              </template>
            </VTooltip>
          </div>
        </div>

        <div>
          <div
            class="c-control-item__status"
            :class="[
              item.contactor == 0 ? 'c-control-item__status_contactor' : '',
              item.relay == 1 ? 'c-control-item__status_relay' : '',
            ]"
            :title="
              $t('itf.contactor_status', {
                contactor: item.contactor ? $t('itf.off') : $t('itf.on'),
                relay: item.relay ? $t('itf.on') : $t('itf.off'),
              })
            "
          ></div>
        </div>

        <div class="c-control-item__icons">
          <VIcons v-if="item.mode == 1" type="calendar_clock" bold :title="$t('itf.auto_mode')" />
          <VIcons v-else type="touch_app" bold :title="$t('itf.manual_mode')" />
        </div>

        <div>
          <div
            class="c-control-item__buttons"
            :class="{ disabled: !isPermissions(['DEVICE_CONTROL']) }"
          >
            <VBtn density="compact" color="warning" @click="clickHandler(item.id - 1, 'mode', 1)">
              {{ $t('itf.auto') }}
            </VBtn>
            <VBtn density="compact" color="warning" @click="clickHandler(item.id - 1, 'switch', 1)">
              {{ $t('itf.on_short') }}
            </VBtn>
            <VBtn density="compact" color="warning" @click="clickHandler(item.id - 1, 'switch', 0)">
              {{ $t('itf.off_short') }}
            </VBtn>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
